import { useEffect, useState } from "react";
import { mergeOwnedNumbers, type PortalNumber } from "./owned-numbers";
import { loadNumberAssignments } from "./number-assignments";
import {
  loadTwilioInventory,
  type TwilioInventoryAccount,
} from "./twilio-inventory";
type Api = <T>(path: string, options?: { signal?: AbortSignal }) => Promise<T>;

export function OwnedNumbersPanel({
  api,
  local,
  onAccount,
  onRefresh,
}: {
  api: Api;
  local: PortalNumber[];
  onAccount: (id: string) => void;
  onRefresh: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(true);
  const [revision, setRevision] = useState(0);
  const [filter, setFilter] = useState("");
  const [localError, setLocalError] = useState("");
  const [assignments, setAssignments] = useState<PortalNumber[]>([]);
  const [assignmentsComplete, setAssignmentsComplete] = useState(false);
  const [assignmentError, setAssignmentError] = useState("");
  const [twilio, setTwilio] = useState<TwilioInventoryAccount[]>([]);
  const [twilioErrors, setTwilioErrors] = useState<string[]>([]);
  const [twilioConfigured, setTwilioConfigured] = useState<boolean | null>(
    null,
  );
  useEffect(() => {
    const abort = new AbortController();
    setBusy(true);
    setTwilio([]);
    setTwilioErrors([]);
    setTwilioConfigured(null);
    setAssignments([]);
    setAssignmentsComplete(false);
    setAssignmentError("");
    void Promise.all([
      loadNumberAssignments(api, abort.signal)
        .then((result) => {
          if (abort.signal.aborted) return;
          setAssignments(result.items);
          setAssignmentsComplete(result.complete);
          setAssignmentError(result.error);
        })
        .catch(() => {
          if (!abort.signal.aborted)
            setAssignmentError(
              "Portal assignments could not be checked. Refresh numbers to retry.",
            );
        }),
      loadTwilioInventory(api, abort.signal)
        .then((result) => {
          if (abort.signal.aborted) return;
          setTwilio(result.accounts);
          setTwilioConfigured(result.configured);
          setTwilioErrors(result.errors);
        })
        .catch(() => {
          if (!abort.signal.aborted)
            setTwilioErrors([
              "Twilio inventory could not be checked. Refresh numbers to retry.",
            ]);
        }),
    ]).finally(() => {
      if (!abort.signal.aborted) setBusy(false);
    });
    return () => abort.abort();
  }, [api, revision]);
  const localRows = assignmentsComplete
    ? assignments
    : [
        ...new Map(
          [...local, ...assignments].map((item) => [item.e164, item]),
        ).values(),
      ];
  const current = localRows.filter(
    (row) => row.account_id !== null || /^Twilio(?: |$)/i.test(row.carrier),
  );
  const all = mergeOwnedNumbers(current, [], [], assignmentsComplete, twilio);
  const query = filter.trim().toLowerCase();
  const digits = query.replace(/[^0-9]/g, "");
  const rows = all.filter(
    (row) =>
      `${row.number} ${row.carrier} ${row.accountName ?? ""} ${row.twilioObservations?.map((item) => item.carrier).join(" ") ?? ""}`
        .toLowerCase()
        .includes(query) ||
      (/^[+\d\s().-]+$/.test(query) &&
        digits.length > 0 &&
        row.number.includes(digits)),
  );
  return (
    <section className="cr-panel">
      <div className="cr-account-heading">
        <div>
          <h2>Your numbers</h2>
          <p>Owned carrier numbers and portal assignments in one list.</p>
        </div>
        <button
          className="cr-button secondary"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setLocalError("");
            try {
              await onRefresh();
            } catch {
              setLocalError(
                "Portal assignments could not be refreshed. Their last loaded values are shown.",
              );
            } finally {
              setRevision((value) => value + 1);
            }
          }}
        >
          Refresh numbers
        </button>
      </div>
      <label className="cr-field">
        <span>Find a number or customer in this list</span>
        <input
          type="search"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
        />
      </label>
      {busy && <p role="status">Loading your owned numbers from Twilio…</p>}
      {localError && (
        <p className="cr-notice error" role="alert">
          {localError}
        </p>
      )}
      {assignmentError && (
        <p className="cr-notice error" role="alert">
          {assignmentError} Missing assignment data is not treated as an
          available number.
        </p>
      )}
      {twilioErrors.map((message) => (
        <p className="cr-notice error" role="alert" key={message}>
          {message} Portal assignments are still shown. Refresh numbers to
          retry.
        </p>
      ))}
      {twilio.map(
        (account) =>
          account.checkedAt && (
            <p className="cr-form-note" key={account.id}>
              Twilio {account.label} checked{" "}
              {new Date(account.checkedAt).toLocaleString()}.
            </p>
          ),
      )}
      <p className="cr-form-note">
        {twilioConfigured === false ? "Twilio is not connected yet. " : ""}A
        number without a portal customer may still carry live calls elsewhere.
        Carrier ownership and portal assignment are checked separately. See the
        customer's service status for call routing.
      </p>
      {rows.length ? (
        <div
          className="cr-table-wrap"
          tabIndex={0}
          role="region"
          aria-label="Owned telephone numbers"
        >
          <table>
            <thead>
              <tr>
                <th scope="col">Telephone number</th>
                <th scope="col">Customer</th>
                <th scope="col">Carrier</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.number}>
                  <td>{row.number}</td>
                  <td>
                    {row.accountId ? (
                      <button
                        className="cr-text-button"
                        onClick={() => onAccount(row.accountId!)}
                      >
                        {row.accountName || "Open customer"}
                      </button>
                    ) : row.assignmentChecked ? (
                      "Not linked at last check"
                    ) : (
                      "Assignment not checked"
                    )}
                  </td>
                  <td>{row.carrier}</td>
                  <td>
                    {row.carrierStatus
                      ? `Carrier: ${row.carrierStatus.replaceAll("_", " ")}`
                      : "Carrier not checked"}
                    {row.portalStatus && (
                      <small className="cr-number-detail">
                        Portal: {row.portalStatus.replaceAll("_", " ")}
                      </small>
                    )}
                    {row.twilioObservations?.map((observation) => (
                      <div
                        className="cr-number-detail"
                        key={observation.carrier}
                      >
                        Also listed in {observation.carrier}:{" "}
                        {observation.status.replaceAll("_", " ")}
                        {observation.connection && (
                          <details>
                            <summary>{observation.carrier} destination</summary>
                            <span>{observation.connection}</span>
                          </details>
                        )}
                      </div>
                    ))}
                    {row.connection && (
                      <details>
                        <summary>Call destination</summary>
                        <span>{row.connection}</span>
                      </details>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        !busy && (
          <p>
            {filter
              ? "No numbers or customers match your search. Clear the search to see the full list."
              : twilioErrors.length
                ? "No local numbers recorded. Carrier inventory could not be loaded."
                : "No owned numbers found in the connected carrier or portal records."}
          </p>
        )
      )}
    </section>
  );
}
